/**
 * Gesture smoothing & detection utilities (Phase 1).
 *
 * Pure, framework-free algorithmic building blocks for the Hand Gesture
 * Controls pipeline:
 *   - {@link LandmarkSmoother}  — EMA smoothing with NaN immunity (H10).
 *   - {@link GestureCooldown}   — per-gesture re-trigger suppression.
 *   - {@link GestureDebouncer}  — consecutive-frame confirmation.
 *   - {@link SwipeDetector}     — sliding-window horizontal swipe detection
 *                                 with dominant-axis filtering.
 *
 * All classes hold their own mutable state and are independently resettable.
 *
 * _Requirements: 2.1, 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 4.4_
 */

import type { Landmark } from '../types/hand-gestures';

/** Coerce a possibly non-finite number to a finite value (default 0). */
function toFinite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

/** True when every coordinate of a landmark is finite (no NaN/Infinity). */
function isFiniteLandmark(landmark: Landmark): boolean {
  return (
    Number.isFinite(landmark.x) &&
    Number.isFinite(landmark.y) &&
    Number.isFinite(landmark.z)
  );
}

/**
 * Exponential Moving Average (EMA) smoother for hand landmarks.
 *
 * Removes per-frame jitter by blending each incoming landmark toward its
 * previous smoothed position: `smoothed += (raw - smoothed) * factor`.
 *
 * NaN immunity (Property 19 / H10): if any coordinate of an incoming landmark
 * is non-finite, that landmark is skipped and its previous finite smoothed
 * value is retained, so non-finite values never leak into the output or the
 * internal state. On the first frame, non-finite coordinates are coerced to 0.
 */
export class LandmarkSmoother {
  private smoothed: Landmark[] = [];
  private readonly factor: number;

  /**
   * @param smoothingFactor EMA factor in (0, 1]. Higher = more responsive,
   *   lower = smoother. Defaults to 0.15.
   */
  constructor(smoothingFactor = 0.15) {
    this.factor = smoothingFactor;
  }

  /**
   * Smooth a frame of landmarks against the retained history.
   *
   * @param landmarks Raw landmarks for the current frame.
   * @returns A new array of finite smoothed landmarks (length preserved).
   */
  update(landmarks: Landmark[]): Landmark[] {
    // First frame: seed history with finite-coerced copies.
    if (this.smoothed.length === 0) {
      this.smoothed = landmarks.map((l) => ({
        x: toFinite(l.x),
        y: toFinite(l.y),
        z: toFinite(l.z),
      }));
      return this.smoothed.map((l) => ({ ...l }));
    }

    for (let i = 0; i < landmarks.length; i++) {
      const raw = landmarks[i];

      // New index with no history: seed it (coercing any non-finite coords).
      if (!this.smoothed[i]) {
        this.smoothed[i] = {
          x: toFinite(raw.x),
          y: toFinite(raw.y),
          z: toFinite(raw.z),
        };
        continue;
      }

      // NaN guard: skip landmarks with any non-finite coordinate, keeping the
      // previous finite smoothed value.
      if (!isFiniteLandmark(raw)) {
        continue;
      }

      const prev = this.smoothed[i];
      prev.x += (raw.x - prev.x) * this.factor;
      prev.y += (raw.y - prev.y) * this.factor;
      prev.z += (raw.z - prev.z) * this.factor;
    }

    return this.smoothed.map((l) => ({ ...l }));
  }

  /** Clear history so the next {@link update} returns raw values again. */
  reset(): void {
    this.smoothed = [];
  }
}

/**
 * Per-gesture cooldown gate that prevents a gesture from re-firing until its
 * configured window has fully elapsed. Cooldowns are tracked independently per
 * gesture name (Property 11) and timed via `Date.now()` (a monotonic-enough
 * wall clock that also responds to test fake-timers).
 */
export class GestureCooldown {
  private readonly lastTrigger = new Map<string, number>();
  private readonly defaultCooldownMs: number;

  /**
   * @param defaultCooldownMs Default cooldown window in milliseconds (800).
   */
  constructor(defaultCooldownMs = 800) {
    this.defaultCooldownMs = defaultCooldownMs;
  }

  /**
   * Whether the given gesture may fire now.
   *
   * @param gesture Gesture name.
   * @param cooldownMs Optional override for this check's cooldown window.
   * @returns `true` if no prior trigger exists or the window has elapsed.
   */
  canTrigger(gesture: string, cooldownMs?: number): boolean {
    const last = this.lastTrigger.get(gesture);
    if (last === undefined) {
      return true;
    }
    const window = cooldownMs ?? this.defaultCooldownMs;
    return Date.now() - last >= window;
  }

  /** Record the current time as the last trigger time for the gesture. */
  trigger(gesture: string): void {
    this.lastTrigger.set(gesture, Date.now());
  }

  /** Clear all cooldown history. */
  reset(): void {
    this.lastTrigger.clear();
  }
}

/**
 * Frame-count debouncer that confirms a gesture only after it has been observed
 * for a required number of consecutive frames (Property 10). Changing the
 * observed gesture (or a `null` frame) restarts the counter.
 */
export class GestureDebouncer {
  private current: string | null = null;
  private count = 0;
  private readonly requiredFrames: number;

  /**
   * @param requiredFrames Consecutive frames needed to confirm (default 3).
   */
  constructor(requiredFrames = 3) {
    this.requiredFrames = requiredFrames;
  }

  /**
   * Feed one frame's gesture observation.
   *
   * @param gesture Observed gesture name, or `null` when no hand is detected.
   * @returns The confirmed gesture name once the threshold is reached
   *   (and on every subsequent same-gesture frame), otherwise `null`.
   */
  update(gesture: string | null): string | null {
    if (gesture === null) {
      this.current = null;
      this.count = 0;
      return null;
    }

    if (gesture === this.current) {
      this.count += 1;
    } else {
      this.current = gesture;
      this.count = 1;
    }

    return this.count >= this.requiredFrames ? gesture : null;
  }

  /** Reset the debouncer to its initial (unconfirmed) state. */
  reset(): void {
    this.current = null;
    this.count = 0;
  }
}

interface SwipeSample {
  x: number;
  y: number | undefined;
  timestamp: number;
}

/**
 * Sliding-window horizontal swipe detector.
 *
 * Maintains a time-bounded window of recent positions and reports a `'left'` or
 * `'right'` swipe when the oldest→latest horizontal displacement exceeds the
 * threshold in a single, consistent direction. Entries older than the window
 * are evicted (Property 7). When vertical coordinates are supplied and the
 * vertical displacement dominates the horizontal one, the swipe is suppressed
 * (dominant-axis filtering, Property 8). Timestamps use `Date.now()` so the
 * sliding window responds correctly to test fake-timers.
 */
export class SwipeDetector {
  private samples: SwipeSample[] = [];
  private readonly windowMs: number;
  private readonly threshold: number;

  /** Max reversal (against the dominant direction) tolerated per step. */
  private static readonly REVERSAL_TOLERANCE = 0.02;
  /**
   * Minimum samples required before a swipe can be detected. The detector
   * primes its sliding window with the first few positions and only evaluates
   * displacement once a full window has accumulated, so a single mid-sequence
   * sample cannot fire early and clear the window prematurely.
   */
  private static readonly MIN_SAMPLES = 4;

  /**
   * @param windowMs Sliding-window retention in milliseconds (default 300).
   * @param threshold Minimum oldest→latest horizontal displacement (default 0.15).
   */
  constructor(windowMs = 300, threshold = 0.15) {
    this.windowMs = windowMs;
    this.threshold = threshold;
  }

  /**
   * Feed a new position sample.
   *
   * @param x Normalized horizontal position (0–1).
   * @param y Optional normalized vertical position (0–1). When provided on the
   *   window's entries and vertical motion dominates, the swipe is suppressed.
   * @returns `'left'`, `'right'`, or `null` if no swipe is detected.
   */
  update(x: number, y?: number): 'left' | 'right' | null {
    const now = Date.now();
    this.samples.push({ x, y, timestamp: now });

    // Evict entries older than the retention window (Property 7).
    this.samples = this.samples.filter((s) => now - s.timestamp <= this.windowMs);

    if (this.samples.length < SwipeDetector.MIN_SAMPLES) {
      return null;
    }

    const oldest = this.samples[0];
    const latest = this.samples[this.samples.length - 1];
    const deltaX = latest.x - oldest.x;

    if (Math.abs(deltaX) < this.threshold) {
      return null;
    }

    const direction: 'left' | 'right' = deltaX > 0 ? 'right' : 'left';

    // Consistent-direction check: no step may reverse beyond the tolerance
    // against the dominant direction.
    for (let i = 1; i < this.samples.length; i++) {
      const step = this.samples[i].x - this.samples[i - 1].x;
      if (direction === 'right' && step < -SwipeDetector.REVERSAL_TOLERANCE) {
        return null;
      }
      if (direction === 'left' && step > SwipeDetector.REVERSAL_TOLERANCE) {
        return null;
      }
    }

    // Dominant-axis filtering (Property 8): only when vertical data is present.
    if (oldest.y !== undefined && latest.y !== undefined) {
      const deltaY = latest.y - oldest.y;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        return null;
      }
    }

    // Successful detection clears history so the next swipe starts fresh.
    this.samples = [];
    return direction;
  }

  /** Clear all recorded position history. */
  reset(): void {
    this.samples = [];
  }
}
